<template>
  <MarketCard v-if="isSuccess(casinoData)">
    <LinkWrapper
      :class="classes.linkWrapper"
      target="_blank"
      role="button"
      :href="ctaLink"
      @click="() => onClick()"
    >
      <div :class="classes.header">
        <Text
          ref="targetEl"
          :class="classes.headerText"
          name="casino-list-header"
          :fontId="fontId"
          tag="p"
          >{{ headerText || "Top Casino Games Today" }}</Text
        >
      </div>
      <ScrollContainer
        :pt="8"
        :pb="0"
        :gap="14"
        :px="8"
        @scroll.once="() => handleScroll()"
      >
        <CasinoListItem
          v-for="(game, i) in casinoData.data.list"
          :key="i"
          :class="classes.listItem"
          :image="game.casino_game_image_url"
          :name="game.casino_game_name"
          :ctaText="game.casino_game_call_to_action || 'Play Now'"
        />
      </ScrollContainer>
    </LinkWrapper>
  </MarketCard>
  <MarketCard v-else>
    <Skeleton :class="classes.skeleton" />
    <Skeleton :class="classes.skeleton" />
    <Skeleton :class="classes.skeleton" />
    <Skeleton :class="classes.skeleton" />
    <Skeleton :class="classes.skeleton" />
    <Skeleton :class="classes.skeleton" />
  </MarketCard>
</template>

<script lang="ts">
import {
  isSuccess,
  isPending,
  isError,
  injectCasinoData
} from "../../../../lib/provideInjectors";
import MarketCard from "../../../styled/MarketCard.vue";

import { computed, defineComponent } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";

import { getCtaButton } from "../../../../hooks/useCtaButton";

import { injectBrand } from "@fresh8/ad-data-composition/src/brand";

import ScrollContainer from "../../list/ScrollContainer.vue";
import CasinoListItem from "./CasinoListItem.vue";

import Skeleton from "../Skeleton.vue";
import { injectCardNumber, useAnalytics } from "../../../../hooks/useAnalytics";
import { useHandleAnalytics } from "../../../../lib/analytics";
import { useHandleCardAnalytics } from "../../../../lib/card-analytics";
import * as generateLinks from "@fresh8/ad-data-composition/src/lib/generateLinks";
import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import { AnalyticEvents } from "@fresh8/types/src/analytics";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";
import LinkWrapper from "../../../../components/LinkWrapper.vue";
import Text from "../../../styled/Text.vue";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";

const useStyles = createUseStyles({
  linkWrapper: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer"
  },
  header: {
    padding: "10px 12px",
    textAlign: "center",
    borderBottom: props => `1px solid ${props.marketBorderColor}`
  },
  headerText: {
    fontSize: "15px"
  },
  listItem: {
    position: "relative",
    "&:after": {
      content: "''",
      position: "absolute",
      height: "1px",
      width: "100%",
      bottom: "-6px",
      backgroundColor: props => props.marketBorderColor
    }
  },
  skeleton: {
    marginBottom: "2px",
    height: "calc(100% / 4)",

    "&:first-of-type": {
      marginBottom: "3px",
      height: "60px",
      borderRadius: "var(--radius)"
    },

    "&:nth-of-type(2)": {
      borderRadius: "var(--radius) var(--radius) 0 0"
    },
    "&:last-of-type": {
      borderRadius: " 0 0 var(--radius) var(--radius)"
    }
  }
});

export default defineComponent({
  name: "CasinoListMPU",
  components: {
    MarketCard,
    ScrollContainer,
    CasinoListItem,
    Text,
    Skeleton,
    LinkWrapper
  },
  setup() {
    const casinoData = injectCasinoData();
    const brandContext = injectBrand()!;
    const betData = injectBetData();
    const $handleAnalytics = useHandleAnalytics();
    const $handleCardAnalytics = useHandleCardAnalytics();
    const previewContext = injectPreview()!;

    const cardNumber = injectCardNumber();

    const { handleClick: handleAnalytics } = useAnalytics(
      {
        cardNumber
      },
      "Casino Card"
    );

    const handleScroll = () => {
      $handleAnalytics(AnalyticEvents.INTERACTED, "Casino List Scroll");
      $handleCardAnalytics(
        AnalyticEvents.INTERACTED,
        "Casino List Scroll",
        undefined,
        cardNumber
      );
    };

    const ctaButton = computed(() => getCtaButton());

    const reactiveStyles = computed(() => {
      return {
        marketBorderColor: brandContext?.getColorStyle(
          brandContext.adTemplate.value?.marketBorderColor
        )
      };
    });

    const href = computed(() =>
      generateLinks.generate(ctaButton.value.url, {
        affiliateId: betData?.affiliateId.value,
        clickUrl: betData?.clickUrl.value,
        operatorKey: betData?.operator.value?.name || ""
      })
    );
    const classes = useStyles(reactiveStyles);

    function onClick() {
      /**
       * We have to have an onclick handler on marketcard div to make entire ad clickable.
       * we cannot use a Anchor tag as that would make the button an "interactive element" inside an
       * Anchor tag which is invalid HTML (despite it rendering fine), percy will have a fit if you nest Achors
       * WE DO WHAT PERCY SAYS CAUSE APPRENTLY IT KNOWS BETTER
       **/

      if (href.value && !previewContext.preview.value.isPreview) {
        window.open(href.value, "_blank");

        handleAnalytics();
      }
    }

    const ctaLink = computed(() =>
      generateLinks.generate(ctaButton.value.url, {
        affiliateId: betData?.affiliateId.value,
        clickUrl: betData?.clickUrl.value,
        operatorKey: betData?.operator.value?.name || ""
      })
    );

    const headerText = computed(() => {
      return brandContext.brand.value?.adTemplate.casino?.list.header.text;
    });

    const { targetEl } = composePopoverForm("Casino List Header", [
      {
        name: "Header Text",
        type: "text",
        get: () => {
          if (brandContext.brand.value?.adTemplate.casino?.list.header.text) {
          }
          return brandContext.brand.value?.adTemplate.casino?.list.header
            .text as string;
        },
        set: (value: string) => {
          if (
            brandContext.brand.value?.adTemplate.casino?.list.header.text !==
            undefined
          ) {
            brandContext.brand.value.adTemplate.casino.list.header.text = value;
          }
        }
      },
      {
        type: "font",
        get: () => {
          return (
            brandContext?.brand?.value?.adTemplate.casino?.list.header.font ||
            brandContext?.brand?.value?.adTemplate.market
              .casinoListHeaderFont ||
            Object.keys(brandContext?.brand.value?.fonts || {})[0]
          );
        },
        set: (value: string) => {
          if (brandContext?.brand?.value?.adTemplate.casino?.list.header.font) {
            brandContext.brand.value.adTemplate.casino.list.header.font = value;
          }
        }
      }
    ]);

    const fontId = computed(
      () =>
        brandContext?.brand?.value?.adTemplate.casino?.list.header.font ||
        brandContext?.brand?.value?.adTemplate.market.casinoListHeaderFont ||
        Object.keys(brandContext?.brand.value?.fonts || {})[0]
    );

    return {
      fontId,
      ctaLink,
      targetEl,
      headerText,
      onClick,
      ctaButton,
      classes,
      isError,
      isSuccess,
      isPending,
      casinoData,
      handleScroll
    };
  }
});
</script>
